import { useNavigate, useLocation } from 'react-router-dom';
import {
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Box,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import BusinessIcon from '@mui/icons-material/Business';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import DevicesIcon from '@mui/icons-material/Devices';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import SettingsIcon from '@mui/icons-material/Settings';
import PersonIcon from '@mui/icons-material/Person';
import { useAuth } from '../../context/AuthContext';

const DRAWER_WIDTH = 260;

const adminNav = [
  { label: 'Dashboard', path: '/', icon: <DashboardIcon /> },
  { label: 'Partners', path: '/partners', icon: <BusinessIcon /> },
  { label: 'API Keys', path: '/api-keys', icon: <VpnKeyIcon /> },
  { label: 'Devices', path: '/devices', icon: <DevicesIcon /> },
  { label: 'Usage Logs', path: '/usage-logs', icon: <ReceiptLongIcon /> },
  { label: 'Analytics', path: '/analytics', icon: <AnalyticsIcon /> },
  { label: 'Settings', path: '/settings', icon: <SettingsIcon /> },
  { label: 'Profile', path: '/profile', icon: <PersonIcon /> },
];

const partnerNav = [
  { label: 'Dashboard', path: '/', icon: <DashboardIcon /> },
  { label: 'API Keys', path: '/api-keys', icon: <VpnKeyIcon /> },
  { label: 'Devices', path: '/devices', icon: <DevicesIcon /> },
  { label: 'Usage Logs', path: '/usage-logs', icon: <ReceiptLongIcon /> },
  { label: 'Analytics', path: '/analytics', icon: <AnalyticsIcon /> },
  { label: 'Settings', path: '/settings', icon: <SettingsIcon /> },
  { label: 'Profile', path: '/profile', icon: <PersonIcon /> },
];

function NavList({ items, onNavigate }) {
  const location = useLocation();
  return (
    <List sx={{ px: 1 }}>
      {items.map(item => {
        const active = location.pathname === item.path;
        return (
          <ListItemButton
            key={item.path}
            selected={active}
            onClick={() => onNavigate(item.path)}
            sx={{
              mb: 0.5,
              borderRadius: 2,
              '&.Mui-selected': {
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                '& .MuiListItemIcon-root': { color: 'inherit' },
                '&:hover': { bgcolor: 'primary.dark' },
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
            <ListItemText primary={item.label} primaryTypographyProps={{ fontWeight: 600 }} />
          </ListItemButton>
        );
      })}
    </List>
  );
}

export default function Sidebar({ mobileOpen, onMobileClose }) {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { isAdmin } = useAuth();
  const items = isAdmin ? adminNav : partnerNav;

  const handleNavigate = path => {
    navigate(path);
    if (isMobile) onMobileClose();
  };

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Toolbar sx={{ px: 2.5, minHeight: 72 }}>
        <Box>
          <Typography variant="h6" fontWeight={800} color="primary">
            Dr. Odin
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Admin Console
          </Typography>
        </Box>
      </Toolbar>
      <NavList items={items} onNavigate={handleNavigate} />
    </Box>
  );

  return (
    <>
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onMobileClose}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { width: DRAWER_WIDTH },
        }}
      >
        {drawer}
      </Drawer>
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', md: 'block' },
          width: DRAWER_WIDTH,
          flexShrink: 0,
          '& .MuiDrawer-paper': { width: DRAWER_WIDTH, boxSizing: 'border-box' },
        }}
        open
      >
        {drawer}
      </Drawer>
    </>
  );
}

export { DRAWER_WIDTH };
